import { MessageCircle, ArrowUpRight, Eye } from "lucide-react";
import { fmtPrice, waLink, displayName } from "@/data/site";
import { ProductImage } from "./ProductImage";
import { useQuickView } from "./QuickView";

export const ProductCard = ({ product, className = "" }) => {
  const openQuickView = useQuickView();
  const discount = product.oldPrice
    ? Math.round((1 - product.price / product.oldPrice) * 100)
    : 0;
  const name = displayName(product.name);
  const message = `Merhaba, Atılmaz Saat üzerinden ${name} (Fiyat: ${fmtPrice(product.price)}) ürünü hakkında bilgi almak ve sipariş vermek istiyorum.`;

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className={`group relative bg-white border border-gold-muted overflow-hidden transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-xl hover:shadow-gold/10 ${className}`}
    >
      <button
        onClick={() => openQuickView(product)}
        data-testid={`product-link-${product.id}`}
        className="block w-full relative aspect-[4/5] bg-white overflow-hidden cursor-pointer"
        aria-label={`${name} hızlı bakış`}
      >
        <ProductImage
          src={product.image}
          alt={name}
          className="h-full w-full object-contain p-5 transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <span className="absolute inset-x-0 bottom-0 flex justify-center pb-3 opacity-0 translate-y-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0 pointer-events-none">
          <span className="inline-flex items-center gap-1.5 bg-emerald/90 backdrop-blur text-ivory text-[11px] font-semibold tracking-wide px-4 py-2 rounded-full">
            <Eye size={13} /> Hızlı Bakış
          </span>
        </span>
        {discount > 0 && (
          <span
            data-testid={`discount-badge-${product.id}`}
            className="absolute top-3 left-3 bg-emerald text-ivory font-mono text-[11px] font-medium tracking-wider px-2.5 py-1"
          >
            -%{discount}
          </span>
        )}
        {!product.available && (
          <span
            data-testid={`stock-badge-${product.id}`}
            className="absolute top-3 right-3 bg-ink/85 text-ivory font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1"
          >
            Tükendi
          </span>
        )}
      </button>

      <div className="p-4 sm:p-5 border-t border-gold-muted/60">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">{product.brand}</p>
        <button onClick={() => openQuickView(product)} className="block w-full text-left group/title">
          <h3 className="mt-1.5 font-serif text-lg sm:text-xl font-medium text-ink leading-snug line-clamp-2 transition-colors group-hover/title:text-gold">
            {name}
          </h3>
        </button>
        <div className="mt-3 flex items-baseline gap-2.5" data-testid={`price-${product.id}`}>
          <span className="text-lg sm:text-2xl font-semibold tracking-tight text-emerald whitespace-nowrap">
            {fmtPrice(product.price)}
          </span>
          {product.oldPrice && (
            <span className="text-xs sm:text-sm text-sage line-through whitespace-nowrap">{fmtPrice(product.oldPrice)}</span>
          )}
        </div>
        <div className="mt-4 flex gap-2">
          {product.available ? (
            <a
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`satin-al-${product.id}`}
              className="satin-al-button flex-1 flex h-11 items-center justify-center gap-2 rounded-full bg-emerald text-[13px] font-semibold text-ivory transition-colors duration-300 hover:bg-ink"
            >
              Satın Al
              <ArrowUpRight size={14} />
            </a>
          ) : (
            <span
              data-testid={`satin-al-disabled-${product.id}`}
              className="flex-1 flex h-11 items-center justify-center rounded-full bg-sand text-[13px] font-semibold text-sage cursor-not-allowed"
            >
              Stokta Yok
            </span>
          )}
          <a
            href={waLink(message)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`whatsapp-ask-${product.id}`}
            aria-label="WhatsApp'tan sor"
            className="h-11 w-11 shrink-0 rounded-full border border-emerald/20 inline-flex items-center justify-center text-emerald transition-colors duration-300 hover:border-emerald"
          >
            <MessageCircle size={16} />
          </a>
        </div>
      </div>
    </article>
  );
};
